import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { Button, Input } from '@ui/components/core'
import { CloseIcon } from '@ui/components/icons'
import { Member } from '@ui/interfaces'
import { useSettings } from '@ui/providers/SettingsProvider'
import { teamService } from '@ui/services'
import { InviteTeamMembersInput } from '@ui/services'
import clsx from 'clsx'

interface InviteTeamMemberFormProps {
  teamMembers: Member[]
  refetch: () => void
  removeTeamMember: (id: string) => Promise<void>
}

export const InviteTeamMemberForm = ({ teamMembers, refetch, removeTeamMember }: InviteTeamMemberFormProps) => {
  const { currentMember, orgMembers, selectedTeam } = useSettings()

  const [email, setEmail] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [filteredOrgMembers, setFilteredOrgMembers] = useState<Member[]>([])

  const handleAddEmail = async (email: string) => {
    if (email.trim() === '') return

    const memberToInvite = filteredOrgMembers.find((member) => member.email === email)
    if (!memberToInvite) return

    await handleInvite({ user_id: memberToInvite.id, team_id: selectedTeam!.id })
    setEmails([...emails, email])
    setEmail('')
    refetch()
  }

  const handleRemoveEmail = async (email: string) => {
    const member = teamMembers.find((member) => member.email === email)
    if (!member) return

    await removeTeamMember(member.id.toString())
    setEmails(emails.filter((e) => e !== email))
    setFilteredOrgMembers(filteredOrgMembers.filter((member) => member.email !== email))
    refetch()
  }

  const { mutateAsync: handleInvite } = useMutation({
    mutationFn: (data: InviteTeamMembersInput) => teamService.inviteTeamMembers(data, currentMember as Member),
  })

  const handleFilterOrgMembers = () => {
    setFilteredOrgMembers(
      orgMembers
        // Filter by email
        .filter((member) => member.email.toLowerCase().includes(email.toLowerCase()))
        // Filter by already invited
        .filter((member) => !emails.includes(member.email))
        // Filter out current user
        .filter((member) => member.email !== currentMember.email)
    )
  }

  const paleColors = [
    '#F4CCCC',
    '#FFE5B4',
    '#D9EAD3',
    '#CFE2F3',
    '#D5A6BD',
    '#FFF2CC',
    '#D9D2E9',
    '#F9CB9C',
    '#B6D7A8',
    '#C9DAF8',
  ]

  return (
    <div className="relative w-full space-y-2 px-6 py-4">
      <div className="flex flex-row space-x-2">
        <div className="relative w-full">
          <Input
            hiddenLabel
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              handleFilterOrgMembers()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddEmail(email)
              }
            }}
            leadingIcon={<EnvelopeIcon className="size-4" />}
            label="Email"
            placeholder="Email"
          />

          {email.length > 0 && (
            <div className="absolute right-0 top-9 z-10 h-auto w-full rounded-md bg-gray-100">
              {filteredOrgMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex cursor-pointer flex-row items-center space-x-2 rounded-md p-4 transition-all duration-300 hover:bg-gray-200"
                  onClick={() => {
                    setEmail(member.email)
                    handleAddEmail(member.email)
                  }}>
                  <div
                    style={{ backgroundColor: paleColors[member.id % paleColors.length] }}
                    className={clsx('flex size-8 items-center justify-center rounded-full')}>
                    <p className="text-md font-bold">
                      {member?.first_name ? member?.first_name[0].toUpperCase() : ''}
                      {member?.last_name ? member?.last_name[0].toUpperCase() : ''}
                    </p>
                  </div>

                  <div className="flex flex-col">
                    <p className="text-xs text-gray-500">
                      {member.first_name} {member.last_name}
                    </p>
                    <p className="text-sm font-medium">{member.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button variant="primary-red" size="small" onClick={() => handleAddEmail(email)}>
          Invite
        </Button>
      </div>
      <div className="flex w-full flex-row flex-wrap space-x-2">
        {emails.map((email) => (
          <div
            className="flex w-fit cursor-pointer flex-row items-center space-x-2 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium transition-all duration-300 hover:bg-gray-200"
            key={email}
            onClick={() => handleRemoveEmail(email)}>
            {email}
            <CloseIcon className="size-3 stroke-gray-900" />
          </div>
        ))}
      </div>
    </div>
  )
}
